import { type Nodes, type Paragraph, type Parent, type PhrasingContent, type Root, type Text } from 'mdast';

import { type WorkspaceAgentMessagePill } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/pillMetadata';
import { type WorkspaceAgentContentExporterMonikerNode } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentContentExporterMonikerNode';
import { type WorkspaceAgentPillNode } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentPillNode';
import { type WorkspaceAgentShareLinkMonikerNode } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentShareLinkMonikerNode';
import { type WorkspaceAgentShowcaseMonikerNode } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentShowcaseMonikerNode';
import { type WorkspaceAgentSkillMonikerNode } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentSkillMonikerNode';
import { type WorkspaceAgentUnknownMonikerNode } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentUnknownMonikerNode';
import {
  findWorkspaceAgentMessagePill,
  isUnresolvedLegacyCommentMoniker,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/findWorkspaceAgentMessagePill';
import { splitPillMonikers } from '~/components/v2/components/workspace/workspace-agent-panel/utils/splitPillMonikers';

/** Code holds content the agent meant literally, so monikers inside it must not become chips. */
const SKIP_TYPES = new Set<Nodes['type']>(['code', 'inlineCode']);

const BLOCK_PILL_TYPES = new Set<WorkspaceAgentMessagePill['type']>(['assetList', 'comment', 'changePreview', 'table']);

const toPillNode = (monikerId: string): WorkspaceAgentPillNode => ({
  data: {
    hName: 'workspace-agent-pill',
    hProperties: { id: monikerId },
  },
  type: 'workspaceAgentPill',
});

const toSkillMonikerNode = (skillId: string): WorkspaceAgentSkillMonikerNode => ({
  data: {
    hName: 'workspace-agent-skill-moniker',
    hProperties: { skillId },
  },
  type: 'workspaceAgentSkillMoniker',
});

const toShareLinkMonikerNode = (shortId: string): WorkspaceAgentShareLinkMonikerNode => ({
  data: {
    hName: 'workspace-agent-share-link-moniker',
    hProperties: { shortId },
  },
  type: 'workspaceAgentShareLinkMoniker',
});

const toShowcaseMonikerNode = (collectionId: string): WorkspaceAgentShowcaseMonikerNode => ({
  data: {
    hName: 'workspace-agent-showcase-moniker',
    hProperties: { collectionId },
  },
  type: 'workspaceAgentShowcaseMoniker',
});

const toContentExporterMonikerNode = (taskId: string): WorkspaceAgentContentExporterMonikerNode => ({
  data: {
    hName: 'workspace-agent-content-exporter-moniker',
    hProperties: { taskId },
  },
  type: 'workspaceAgentContentExporterMoniker',
});

const toUnknownMonikerNode = (monikerType: string, monikerId: string): WorkspaceAgentUnknownMonikerNode => ({
  data: {
    hName: 'workspace-agent-unknown-moniker',
    hProperties: { monikerId, monikerType },
  },
  type: 'workspaceAgentUnknownMoniker',
});

type SplitTextMonikerNode =
  | Text
  | WorkspaceAgentContentExporterMonikerNode
  | WorkspaceAgentPillNode
  | WorkspaceAgentShareLinkMonikerNode
  | WorkspaceAgentShowcaseMonikerNode
  | WorkspaceAgentSkillMonikerNode
  | WorkspaceAgentUnknownMonikerNode;

/** Returns `null` when the text holds no monikers, so untouched nodes keep their identity. */
const splitTextNode = (node: Text): SplitTextMonikerNode[] | null => {
  const segments = splitPillMonikers(node.value);

  if (!segments.some((segment) => segment.type === 'pill' || segment.type === 'unknownMoniker')) {
    return null;
  }

  return segments.map((segment) => {
    if (segment.type === 'text') {
      return { type: 'text', value: segment.value };
    }

    if (segment.type === 'unknownMoniker') {
      return toUnknownMonikerNode(segment.monikerType, segment.monikerId);
    }

    if (segment.entityType === 'skill') {
      return toSkillMonikerNode(segment.monikerId);
    }

    if (segment.entityType === 'shareLink') {
      return toShareLinkMonikerNode(segment.monikerId);
    }

    if (segment.entityType === 'showcase') {
      return toShowcaseMonikerNode(segment.monikerId);
    }

    if (segment.entityType === 'contentExporter') {
      return toContentExporterMonikerNode(segment.monikerId);
    }

    return toPillNode(segment.monikerId);
  });
};

const isWhitespaceText = (node: Nodes): boolean => node.type === 'text' && node.value.trim() === '';

const isBlockWorkspaceAgentPill = (
  pills: readonly WorkspaceAgentMessagePill[],
  node: WorkspaceAgentPillNode,
): boolean => {
  const monikerId = node.data.hProperties.id;
  const pill = findWorkspaceAgentMessagePill(pills, monikerId);

  if (pill !== undefined) {
    return BLOCK_PILL_TYPES.has(pill.type);
  }

  return isUnresolvedLegacyCommentMoniker(monikerId, pill);
};

const paragraphHasVisibleContent = (children: PhrasingContent[]): boolean =>
  children.some((child) => child.type !== 'text' || child.value.trim() !== '');

/**
 * Lifts comment and change-preview pills out of `<p>` ancestors so their card chrome (a `<button>` or
 * `<div>`) is not invalidly nested — browsers hoist those nodes and the card can land on the composer.
 */
const splitParagraphAroundBlockPills = (
  paragraph: Paragraph,
  pills: readonly WorkspaceAgentMessagePill[],
): (Paragraph | WorkspaceAgentPillNode)[] => {
  const result: (Paragraph | WorkspaceAgentPillNode)[] = [];
  let currentInline: PhrasingContent[] = [];

  const flushParagraph = () => {
    if (!paragraphHasVisibleContent(currentInline)) {
      currentInline = [];
      return;
    }

    result.push({ type: 'paragraph', children: currentInline });
    currentInline = [];
  };

  for (const child of paragraph.children) {
    if (child.type === 'workspaceAgentPill' && isBlockWorkspaceAgentPill(pills, child)) {
      flushParagraph();
      result.push(child);
      continue;
    }

    currentInline.push(child);
  }

  flushParagraph();

  return result;
};

const splitBlockPillsOutOfParagraphs = (parent: Parent, pills: readonly WorkspaceAgentMessagePill[]): void => {
  const children = parent.children;

  for (let index = 0; index < children.length; index++) {
    const node = children[index];

    if ('children' in node) {
      splitBlockPillsOutOfParagraphs(node, pills);
    }

    if (node.type !== 'paragraph') {
      continue;
    }

    const containsBlockPill = node.children.some(
      (child): child is WorkspaceAgentPillNode =>
        child.type === 'workspaceAgentPill' && isBlockWorkspaceAgentPill(pills, child),
    );

    if (!containsBlockPill) {
      continue;
    }

    const replacement = splitParagraphAroundBlockPills(node, pills);
    children.splice(index, 1, ...replacement);
    index += replacement.length - 1;
  }
};

const isPillOnlyParagraph = (node: Nodes): boolean => {
  if (node.type !== 'paragraph' || !('children' in node)) {
    return false;
  }

  return node.children.every((child) => child.type === 'workspaceAgentPill' || isWhitespaceText(child));
};

/** Hoists pill-only paragraphs so inline chips are not wrapped in markdown `<p>` tags. */
const unwrapPillOnlyParagraphs = (parent: Parent): void => {
  const children = parent.children;

  for (let index = 0; index < children.length; index++) {
    const node = children[index];

    if ('children' in node) {
      unwrapPillOnlyParagraphs(node);
    }

    if (!isPillOnlyParagraph(node) || !('children' in node)) {
      continue;
    }

    const pillNodes = node.children.filter(
      (child): child is WorkspaceAgentPillNode => child.type === 'workspaceAgentPill',
    );
    children.splice(index, 1, ...pillNodes);
    index += pillNodes.length - 1;
  }
};

const walk = (parent: Parent): void => {
  const children = parent.children;

  for (let index = 0; index < children.length; index++) {
    const node = children[index];

    if (SKIP_TYPES.has(node.type)) {
      continue;
    }

    if (node.type === 'text') {
      const replacement = splitTextNode(node);

      if (replacement) {
        children.splice(index, 1, ...replacement);
        index += replacement.length - 1;
      }

      continue;
    }

    if ('children' in node) {
      walk(node);
    }
  }
};

/**
 * Remark plugin that replaces inline `@[pill:<id>]` text with a custom node, rendered downstream as
 * a `<workspace-agent-pill>` element.
 */
export const remarkWorkspaceAgentPill = (pills: readonly WorkspaceAgentMessagePill[]) => {
  return (tree: Root): void => {
    walk(tree);
    splitBlockPillsOutOfParagraphs(tree, pills);
    unwrapPillOnlyParagraphs(tree);
  };
};
